import i18n from "@/i18n";

/** BCP 47 tag used for dates and numbers. */
export function currentLocale(): string {
  if (i18n.resolvedLanguage) return i18n.resolvedLanguage;
  return "en-US";
}

export function formatDate(iso: string | Date | undefined, locale = currentLocale()): string {
  if (!iso) return "";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

export function formatDateTime(iso: string | Date | undefined, locale = currentLocale()): string {
  if (!iso) return "";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export function truncate(text: string, max = 140): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

export function initials(name: string | undefined, fallback = "?"): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * How many decimals a currency's amounts have (its minor unit): EUR 2, JPY 0, BHD 3. 2 when the
 * currency is missing or unknown.
 */
export function currencyDigits(currency?: string | null): number {
  try {
    if (!currency) return 2;
    return (
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * "1.234,50" in the invoice's number locale, with the currency's decimals ("1.235" for JPY);
 * unknown locales fall back to en-US.
 */
export function formatNumber(
  value: number | null | undefined,
  locale?: string,
  currency?: string | null,
): string {
  const digits = currencyDigits(currency);
  const options = { minimumFractionDigits: digits, maximumFractionDigits: digits };
  try {
    return new Intl.NumberFormat(locale || "en-US", options).format(value ?? 0);
  } catch {
    return new Intl.NumberFormat("en-US", options).format(value ?? 0);
  }
}

/** "1.234,50 EUR": invoice amounts as printed on lists (number locale + ISO code). */
export function formatAmount(value: number | null | undefined, currency?: string, locale?: string) {
  return `${formatNumber(value, locale, currency)} ${currency ?? ""}`.trim();
}

/** "€1,234.50": amounts in the UI language (dashboards). */
export function formatMoney(
  value: number,
  currency: string,
  locale = currentLocale(),
  fractionDigits = currencyDigits(currency),
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  } catch {
    return formatAmount(value, currency, locale);
  }
}

/** Rich text (TipTap HTML) as one plain line, e.g. an address in a table cell. */
export function stripHtml(html?: string | null): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>\s*<p[^>]*>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** "2026-01-15" <-> Date in local time (no timezone shifts). */
export const parseDay = (value: string) => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
};
export const toDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
